import React, { useState } from 'react';
import axios from 'axios';

import Button from '~/components/core/Atomic/Buttons/Button';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import AddIcon from '~/components/icons/AddIcon';
import PencilIcon from '~/components/icons/PencilIcon';
import InlineIconButton from '~/components/InlineIconButton';
import EmailAcknowledgmentBuilderContainer from '~/components/SystemConfiguration/EmailConfiguration/EmailAcknowledgmentBuilderContainer';
import type {
  EmailAcknowledgmentConfigurationProps,
  EmailAutoAcknowledgmentBaseData,
} from '~/components/SystemConfiguration/EmailConfiguration/emailConfigurationTypes';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import Switch, { type SwitchChangeEvent } from '~/components/ui/atoms/Switch';
import { reportAxiosError } from '~/Utils';

const BASE_PATH = '/api/v1/email_configuration/organizations';

const acknowledgmentLoader = () => {
  return <SkeletonTable rowsCount={1} columnsCount={1} />;
};

const EmailAcknowledgmentConfiguration: React.FC<EmailAcknowledgmentConfigurationProps> = ({
  isLoading,
  isError,
  emailAcknowledgmentConfiguration,
  isAcknowledgmentEnabled,
  reloadData,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const [isEditEmailAcknowledgment, setIsEditEmailAcknowledgment] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleOpenAcknowledgmentEditor = () => {
    setIsEditEmailAcknowledgment(true);
  };

  const handleCloseEditAcknowledgmentDialog = () => {
    setIsEditEmailAcknowledgment(false);
  };

  const handleAcknowledgmentSubmit = async (values: EmailAutoAcknowledgmentBaseData, acknowledgmentId?: number) => {
    setIsSubmitting(true);
    try {
      const method = acknowledgmentId ? 'patch' : 'post';
      const requestData = method === 'post' ? values : { ...values, is_enabled: isAcknowledgmentEnabled };

      await axios[method](`${BASE_PATH}/${organization.id}/auto_acknowledgment_configuration`, requestData);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnabledAcknowledgmentChange = async (event: SwitchChangeEvent) => {
    try {
      await axios.patch(`${BASE_PATH}/${organization.id}/auto_acknowledgment_configuration`, {
        ...emailAcknowledgmentConfiguration,
        is_enabled: event.target.checked,
      });
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  return (
    <>
      <div className="m-20">
        <LoadingSwitch isLoading={isLoading} isError={isError} LoadingComponent={acknowledgmentLoader}>
          <div className="flex items-center justify-between rounded-lg bg-white p-4">
            <div className="w-full p-24">
              <Heading variant={Heading.TYPES.H2} className="mb-20">
                Acknowledgment Email
              </Heading>
              <Text
                variant={Text.VARIANTS.SM}
                weight={Text.WEIGHTS.REGULAR}
                colorVariant={Text.COLOR_VARIANTS.SECONDARY}
              >
                Automatically sends a confirmation email to external senders when their message is received
              </Text>
            </div>
            <div className="px-24">
              {emailAcknowledgmentConfiguration ? (
                <div className="flex gap-8">
                  <Switch
                    onChange={(event) => handleEnabledAcknowledgmentChange(event)}
                    disabled={isSubmitting}
                    checked={isAcknowledgmentEnabled}
                  />
                  <InlineIconButton
                    className="m-4 hover:text-teal-700"
                    iconColor="currentColor"
                    icon={PencilIcon}
                    onClick={handleOpenAcknowledgmentEditor}
                    tooltipTitle="Edit"
                    disabled={!isAcknowledgmentEnabled || isSubmitting}
                  />
                </div>
              ) : (
                <Button
                  color="primary"
                  disabled={false}
                  onClick={handleOpenAcknowledgmentEditor}
                  fullWidth={true}
                  startIcon={<AddIcon iconColor="currentColor" />}
                  className="whitespace-nowrap"
                >
                  Add template
                </Button>
              )}
            </div>
          </div>
        </LoadingSwitch>
      </div>

      {isEditEmailAcknowledgment && (
        <EmailAcknowledgmentBuilderContainer
          onClose={handleCloseEditAcknowledgmentDialog}
          onEmailAcknowledgmentSubmit={handleAcknowledgmentSubmit}
          emailAcknowledgmentData={emailAcknowledgmentConfiguration}
        />
      )}
    </>
  );
};

export default EmailAcknowledgmentConfiguration;
